import { useEffect, useMemo, useState } from "react";
import type {
  AnexoSuporte, ChamadoSuporte, MensagemSuporte, TipoChamadoSuporte,
} from "@ida/core";
import { ROTULOS_STATUS_SUPORTE, ROTULOS_TIPO_SUPORTE } from "@ida/core";
import {
  collection, limit, onSnapshot, orderBy, query, where,
} from "firebase/firestore";
import { chamar, db } from "../firebase.js";
import { evento, sessaoTelemetria } from "../telemetria.js";
import { Botao } from "./ui.js";
import "./widget-suporte.css";

const criarSuporte = chamar<{
  tipo: TipoChamadoSuporte;
  texto: string;
  origem: {
    pagina: string; navegador: string; diagnosticoId: string; relatoIniciadoEm: string;
    sessaoPosthog?: string; fusoHorario?: string; tela: string; online: boolean;
  };
  anexos?: AnexoUpload[];
}, { chamadoId: string }>("criarSuporte");

const enviarMensagem = chamar<{
  chamadoId: string; texto: string; anexos?: AnexoUpload[];
}, { mensagemId: string }>("enviarMensagemSuporte");

const pedirUpload = chamar<{ mimeType: string; tamanho: number }, {
  url: string; caminho: string;
}>("pedirUploadSuporteUrl");

const pedirLinkAnexo = chamar<{
  chamadoId: string; mensagemId: string; anexoId: string;
}, { url: string }>("linkAnexoSuporte");

interface AnexoUpload {
  caminhoUpload: string;
  nome: string;
  mimeType: string;
  tamanho: number;
}

const MAX_ARQUIVOS = 3;
const MAX_ARQUIVO_BYTES = 25 * 1024 * 1024;
const MAX_TOTAL_BYTES = 50 * 1024 * 1024;
const MIME_ACEITOS = new Set([
  "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif",
  "video/mp4", "video/quicktime", "video/x-m4v", "video/webm",
]);
const ACCEPT_ANEXOS = [...MIME_ACEITOS].join(",");
const MIME_POR_EXTENSAO: Record<string, string> = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp",
  heic: "image/heic", heif: "image/heif", mp4: "video/mp4", mov: "video/quicktime",
  m4v: "video/x-m4v", webm: "video/webm",
};

const EVENTO_ABRIR = "alugando:abrir-suporte";

export function abrirSuporte(tipo?: TipoChamadoSuporte): void {
  window.dispatchEvent(new CustomEvent(EVENTO_ABRIR, { detail: { tipo } }));
}

function dataCurta(valor: string): string {
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  }).format(data);
}

function tamanhoArquivo(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function novoDiagnosticoId(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function mimeDoArquivo(arquivo: File): string | undefined {
  if (MIME_ACEITOS.has(arquivo.type)) return arquivo.type;
  const extensao = arquivo.name.split(".").at(-1)?.toLowerCase() ?? "";
  return MIME_POR_EXTENSAO[extensao];
}

async function subirAnexos(arquivos: File[]): Promise<AnexoUpload[]> {
  return Promise.all(arquivos.map(async (arquivo) => {
    const mimeType = mimeDoArquivo(arquivo);
    if (!mimeType) throw new Error("Formato de anexo inválido.");
    const destino = await pedirUpload({ mimeType, tamanho: arquivo.size });
    const resposta = await fetch(destino.url, {
      method: "PUT", headers: { "Content-Type": mimeType }, body: arquivo,
    });
    if (!resposta.ok) throw new Error("Não consegui enviar um dos anexos.");
    return { caminhoUpload: destino.caminho, nome: arquivo.name,
      mimeType, tamanho: arquivo.size };
  }));
}

const placeholders: Record<TipoChamadoSuporte, string> = {
  duvida: "Como podemos ajudar?",
  sugestao: "Que melhoria faria diferença para você?",
  problema: "O que aconteceu e o que você estava fazendo?",
};

export function WidgetSuporte({ uid }: { uid: string }) {
  const [aberto, setAberto] = useState(false);
  const [tela, setTela] = useState<"lista" | "novo" | "conversa">("lista");
  const [tipo, setTipo] = useState<TipoChamadoSuporte>("duvida");
  const [chamados, setChamados] = useState<ChamadoSuporte[]>([]);
  const [chamadoId, setChamadoId] = useState<string | null>(null);
  const [mensagens, setMensagens] = useState<MensagemSuporte[]>([]);
  const [texto, setTexto] = useState("");
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [relatoIniciadoEm, setRelatoIniciadoEm] = useState(() => new Date().toISOString());
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const consulta = query(collection(db, "suporteChamados"), where("uid", "==", uid),
      orderBy("atualizadoEm", "desc"), limit(30));
    return onSnapshot(consulta, (snap) => {
      setChamados(snap.docs.map((d) => d.data() as ChamadoSuporte));
    }, () => setErro("Não consegui carregar seus atendimentos."));
  }, [uid]);

  useEffect(() => {
    if (!chamadoId) { setMensagens([]); return; }
    const consulta = query(collection(db, "suporteChamados", chamadoId, "mensagens"),
      orderBy("criadoEm", "asc"), limit(100));
    return onSnapshot(consulta, (snap) => {
      setMensagens(snap.docs.map((d) => d.data() as MensagemSuporte));
      setErro(null);
    }, () => setErro("Não consegui carregar esta conversa."));
  }, [chamadoId]);

  useEffect(() => {
    const aoAbrir = (eventoRecebido: Event) => {
      const detalhe = (eventoRecebido as CustomEvent<{ tipo?: TipoChamadoSuporte }>).detail;
      setAberto(true);
      setErro(null);
      if (detalhe?.tipo) {
        setTipo(detalhe.tipo);
        setTela("novo");
        setChamadoId(null);
        setArquivos([]);
        setRelatoIniciadoEm(new Date().toISOString());
      } else setTela("lista");
      evento("suporte_widget_aberto", { entrada: detalhe?.tipo ?? "geral" });
    };
    window.addEventListener(EVENTO_ABRIR, aoAbrir);
    const pelaUrl = new URLSearchParams(window.location.search).get("suporte");
    if (pelaUrl) {
      setAberto(true); setTela("conversa"); setChamadoId(pelaUrl.slice(0, 120));
    }
    return () => window.removeEventListener(EVENTO_ABRIR, aoAbrir);
  }, []);

  useEffect(() => {
    if (!aberto) return;
    const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") setAberto(false); };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [aberto]);

  const selecionado = chamados.find((c) => c.id === chamadoId);
  const abertos = useMemo(() => chamados.filter((c) => c.status !== "concluido"), [chamados]);
  const historico = useMemo(() => chamados.filter((c) => c.status === "concluido"), [chamados]);

  function abrirConversa(id: string) {
    setChamadoId(id); setTela("conversa"); setTexto(""); setArquivos([]); setErro(null);
    evento("suporte_conversa_aberta");
  }

  function adicionarArquivos(lista: FileList | null) {
    if (!lista) return;
    const novos = [...lista];
    if (arquivos.length + novos.length > MAX_ARQUIVOS) {
      setErro("Envie no máximo 3 fotos ou vídeos por mensagem."); return;
    }
    if (novos.some((arquivo) => !mimeDoArquivo(arquivo))) {
      setErro("Use fotos JPG, PNG, WebP ou HEIC, ou vídeos MP4, MOV ou WebM."); return;
    }
    if (novos.some((arquivo) => arquivo.size <= 0 || arquivo.size > MAX_ARQUIVO_BYTES)) {
      setErro("Cada foto ou vídeo pode ter até 25 MB."); return;
    }
    if ([...arquivos, ...novos].reduce((total, arquivo) => total + arquivo.size, 0)
        > MAX_TOTAL_BYTES) {
      setErro("Os anexos da mensagem podem somar até 50 MB."); return;
    }
    setArquivos((atuais) => [...atuais, ...novos]);
    setErro(null);
  }

  async function abrirAnexo(mensagemId: string, anexo: AnexoSuporte) {
    const janela = window.open("about:blank", "_blank");
    if (janela) janela.opener = null;
    try {
      if (!chamadoId) throw new Error("conversa ausente");
      const { url } = await pedirLinkAnexo({ chamadoId, mensagemId, anexoId: anexo.id });
      if (janela) janela.location.replace(url);
      else window.location.assign(url);
      evento("suporte_anexo_aberto", { tipo: anexo.tipo });
    } catch {
      janela?.close();
      setErro("Não consegui abrir o anexo. Tente novamente.");
    }
  }

  async function criar() {
    if (texto.trim().length < 3) { setErro("Conte um pouco mais para a gente ajudar."); return; }
    setEnviando(true); setErro(null);
    try {
      const anexos = await subirAnexos(arquivos);
      const sessaoPosthog = await sessaoTelemetria();
      const resultado = await criarSuporte({
        tipo, texto,
        origem: {
          pagina: window.location.pathname,
          navegador: navigator.userAgent.slice(0, 160),
          diagnosticoId: novoDiagnosticoId(),
          relatoIniciadoEm,
          ...(sessaoPosthog ? { sessaoPosthog } : {}),
          fusoHorario: Intl.DateTimeFormat().resolvedOptions().timeZone,
          tela: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}`,
          online: navigator.onLine,
        },
        ...(anexos.length ? { anexos } : {}),
      });
      setChamadoId(resultado.chamadoId);
      setTela("conversa");
      setTexto("");
      setArquivos([]);
      evento("suporte_chamado_enviado", { tipo, quantidade_anexos: anexos.length });
    } catch {
      setErro("Não consegui enviar agora. Tente novamente.");
      evento("suporte_envio_falhou", { etapa: "novo", tipo });
    } finally { setEnviando(false); }
  }

  async function responder() {
    if (!chamadoId || !texto.trim()) return;
    setEnviando(true); setErro(null);
    try {
      const anexos = await subirAnexos(arquivos);
      await enviarMensagem({ chamadoId, texto, ...(anexos.length ? { anexos } : {}) });
      setTexto("");
      setArquivos([]);
      evento("suporte_mensagem_enviada", {
        tipo: selecionado?.tipo ?? "desconhecido", quantidade_anexos: anexos.length,
      });
    } catch {
      setErro("Não consegui enviar a mensagem. Tente novamente.");
      evento("suporte_envio_falhou", { etapa: "continuidade" });
    } finally { setEnviando(false); }
  }

  if (!aberto) return null;

  return (
    <div className="suporte-widget" data-ph-mask>
      <button className="suporte-widget__fundo" aria-label="Fechar suporte"
        onClick={() => setAberto(false)} />
      <section className="suporte-widget__painel" role="dialog" aria-modal="true"
        aria-labelledby="suporte-titulo">
        <header className="suporte-widget__topo">
          <div>
            {(tela !== "lista") && (
              <button className="suporte-widget__voltar" onClick={() => {
                setTela("lista"); setChamadoId(null); setTexto(""); setArquivos([]); setErro(null);
              }}>← Atendimentos</button>
            )}
            <h2 id="suporte-titulo">
              {tela === "lista" ? "Ajuda" : tela === "novo"
                ? ROTULOS_TIPO_SUPORTE[tipo] : selecionado?.assunto ?? "Atendimento"}
            </h2>
          </div>
          <button className="suporte-widget__fechar" aria-label="Fechar"
            onClick={() => setAberto(false)}>×</button>
        </header>

        {erro && <p className="suporte-widget__erro">{erro}</p>}

        {tela === "lista" && (
          <div className="suporte-widget__conteudo">
            <div className="suporte-widget__tipos">
              {(Object.keys(ROTULOS_TIPO_SUPORTE) as TipoChamadoSuporte[]).map((item) => (
                <button key={item} onClick={() => {
                  setTipo(item); setTela("novo"); setArquivos([]); setErro(null);
                  setRelatoIniciadoEm(new Date().toISOString());
                }}>
                  <strong>{ROTULOS_TIPO_SUPORTE[item]}</strong>
                  <span>{placeholders[item]}</span>
                </button>
              ))}
            </div>

            {!!abertos.length && <ListaChamados titulo="Em aberto" itens={abertos}
              aoAbrir={abrirConversa} />}
            {!!historico.length && <ListaChamados titulo="Histórico" itens={historico}
              aoAbrir={abrirConversa} />}
            {!chamados.length && (
              <p className="suporte-widget__vazio">Seus atendimentos aparecerão aqui.</p>
            )}
          </div>
        )}

        {tela === "novo" && (
          <div className="suporte-widget__conteudo suporte-widget__novo">
            <div className="suporte-widget__seletor">
              {(Object.keys(ROTULOS_TIPO_SUPORTE) as TipoChamadoSuporte[]).map((item) => (
                <button key={item} className={tipo === item ? "ativo" : ""}
                  onClick={() => {
                    setTipo(item); setErro(null);
                    if (item !== "problema") setArquivos([]);
                  }}>{ROTULOS_TIPO_SUPORTE[item]}</button>
              ))}
            </div>
            <label htmlFor="suporte-nova-mensagem">{placeholders[tipo]}</label>
            <textarea id="suporte-nova-mensagem" autoFocus value={texto}
              onChange={(e) => setTexto(e.target.value)} maxLength={4_000} rows={8}
              placeholder="Escreva do seu jeito. Nós veremos o contexto da sua conta." />
            {tipo === "problema" && <SeletorAnexos arquivos={arquivos}
              enviando={enviando} aoAdicionar={adicionarArquivos}
              aoRemover={(indice) => setArquivos((atuais) =>
                atuais.filter((_, item) => item !== indice))} />}
            <p className="suporte-widget__privacidade">
              O agente recebe um resumo da conta sem CPF, documentos ou dados bancários.
              Fotos e vídeos ficam privados para a revisão humana e não são enviados ao agente.
            </p>
            <Botao onClick={() => void criar()} disabled={enviando || texto.trim().length < 3}
              bloco>{enviando ? "Enviando…" : "Enviar para a gente"}</Botao>
          </div>
        )}

        {tela === "conversa" && (
          <div className="suporte-widget__conversa">
            {selecionado && (
              <div className="suporte-widget__estado">
                <span>{ROTULOS_STATUS_SUPORTE[selecionado.status]}</span>
                <small>{selecionado.status === "aguardando_revisao"
                  ? "Estamos revisando uma resposta para você."
                  : selecionado.status === "aguardando_rascunho"
                    ? "Recebemos sua mensagem e estamos analisando."
                    : selecionado.status === "aguardando_usuario"
                      ? "Você pode continuar a conversa abaixo."
                      : "Você pode escrever novamente para reabrir."}</small>
              </div>
            )}
            <div className="suporte-widget__mensagens" aria-live="polite">
              {mensagens.map((m) => (
                <div key={m.id} className={`suporte-widget__mensagem suporte-widget__mensagem--${m.autor}`}>
                  <span>{m.autor === "usuario" ? "Você" : "Alugando.com"}</span>
                  <p>{m.texto}</p>
                  {!!m.anexos?.length && <div className="suporte-widget__anexos-enviados">
                    {m.anexos.map((anexo) => (
                      <button key={anexo.id} onClick={() => void abrirAnexo(m.id, anexo)}>
                        <span aria-hidden="true">{anexo.tipo === "imagem" ? "▧" : "▷"}</span>
                        <span>{anexo.nome}</span><small>{tamanhoArquivo(anexo.bytes)}</small>
                      </button>
                    ))}
                  </div>}
                  <time>{dataCurta(m.criadoEm)}</time>
                </div>
              ))}
            </div>
            <div className="suporte-widget__responder">
              <div>
                <textarea value={texto} onChange={(e) => setTexto(e.target.value)}
                  maxLength={4_000} rows={3} placeholder="Continuar esta conversa" />
                {selecionado?.tipo === "problema" && <SeletorAnexos arquivos={arquivos}
                  enviando={enviando} compacto aoAdicionar={adicionarArquivos}
                  aoRemover={(indice) => setArquivos((atuais) =>
                    atuais.filter((_, item) => item !== indice))} />}
              </div>
              <Botao onClick={() => void responder()} disabled={enviando || !texto.trim()}>
                {enviando ? "Enviando…" : "Enviar"}
              </Botao>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function SeletorAnexos({ arquivos, enviando, compacto = false, aoAdicionar, aoRemover }: {
  arquivos: File[];
  enviando: boolean;
  compacto?: boolean;
  aoAdicionar: (arquivos: FileList | null) => void;
  aoRemover: (indice: number) => void;
}) {
  return <div className={`suporte-widget__anexar${compacto ? " suporte-widget__anexar--compacto" : ""}`}>
    <label className={enviando || arquivos.length >= MAX_ARQUIVOS ? "desabilitado" : ""}>
      <input type="file" multiple accept={ACCEPT_ANEXOS}
        disabled={enviando || arquivos.length >= MAX_ARQUIVOS}
        onChange={(e) => { aoAdicionar(e.currentTarget.files); e.currentTarget.value = ""; }} />
      <span aria-hidden="true">＋</span> Adicionar foto ou vídeo
    </label>
    {!compacto && <small>Até 3 arquivos, 25 MB cada e 50 MB no total.</small>}
    {!!arquivos.length && <div className="suporte-widget__arquivos">
      {arquivos.map((arquivo, indice) => <div key={`${arquivo.name}-${arquivo.size}-${indice}`}>
        <span>{mimeDoArquivo(arquivo)?.startsWith("image/") ? "Foto" : "Vídeo"}</span>
        <strong>{arquivo.name}</strong><small>{tamanhoArquivo(arquivo.size)}</small>
        <button type="button" aria-label={`Remover ${arquivo.name}`}
          disabled={enviando} onClick={() => aoRemover(indice)}>×</button>
      </div>)}
    </div>}
  </div>;
}

function ListaChamados({ titulo, itens, aoAbrir }: {
  titulo: string;
  itens: ChamadoSuporte[];
  aoAbrir: (id: string) => void;
}) {
  return (
    <section className="suporte-widget__lista">
      <h3>{titulo}</h3>
      {itens.map((chamado) => (
        <button key={chamado.id} onClick={() => aoAbrir(chamado.id)}>
          <span>
            <strong>{chamado.assunto}</strong>
            <small>{ROTULOS_STATUS_SUPORTE[chamado.status]}</small>
          </span>
          <time>{dataCurta(chamado.ultimaMensagemEm)}</time>
        </button>
      ))}
    </section>
  );
}
